import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {globalNamespace} from '../../../../../../../../../src/helpers/models/namespaceHelpers';
import {batch} from '../../../../../../../../../src/helpers/reduxHelpers';
import {
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../../../../../src/helpers/reselectHelpers';
import {CollapsedItemKeyPrefixesEnum} from '../../../../../../../../../src/models/api/localPreferencesModel';
import {jobDeletedAction} from '../../../../../../../../../src/store/data/jobs/jobsActions';
import {getImportContactsCsvJobs} from '../../../../../../../../../src/store/data/jobs/jobsSelectors';
import {toggleExpandItemAction} from '../../../../../../../../../src/store/data/localPreferences/localPreferencesActions';
import {getFindIsExpanded} from '../../../../../../../../../src/store/data/localPreferences/localPreferencesSelectors';
import {getUserNamespace} from '../../../../../../../../../src/store/data/teammates/teammatesSelectors';
import {
  makeGetContactsCSVFileState,
  makeGetContactsManagerParameters,
  makeGetContactsManagerParameterType,
} from '../../../../../../../../../src/store/panels/contacts/contactsManager/contactsManagerPanelSelectors';
import {
  contactsManagerClearFileAction,
  contactsManagerUploadContactsCsvAsyncAction,
} from '../../../../../../../../../src/store/panels/contacts/contactsManager/file/contactsManagerFileStateActions';
import {ContactsManagerParametersTypesEnum} from '../../../../../../../../../src/store/panels/contacts/contactsManager/parameters/contactsManagerParameters';
import {ContactsManagerPanelId} from '../../../../../../../../../src/store/panels/panelStateBase';
import {ImportContactsCSVDialog} from './importContactsCSVDialog';

/*
 * Constants.
 */

const infoBoxExpandedKey = `${CollapsedItemKeyPrefixesEnum.IMPORT_CONTACTS_CSV}infoBox`;
const isInfoBoxExpandedDefault = true;

/*
 * Props.
 */

interface ImportContactsCsvDialogContainerProps {
  panelId: ContactsManagerPanelId;
}

/*
 * Selectors.
 */

const getIsInfoBoxExpanded = createSelector(getFindIsExpanded, (findIsExpanded) =>
  findIsExpanded(infoBoxExpandedKey, isInfoBoxExpandedDefault),
);

function makeGetInitialNamespace() {
  return createSelector([makeGetContactsManagerParameters()], (parameters) => {
    if (parameters.type === ContactsManagerParametersTypesEnum.PRIVATE_CONTACTS) {
      return parameters.namespace;
    }
    return globalNamespace;
  });
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ImportContactsCsvDialogContainerProps>()(() => ({
  fileState: makeGetContactsCSVFileState(),
  importContactsCSVJobs: getImportContactsCsvJobs,
  isInfoBoxExpanded: getIsInfoBoxExpanded,
  userNamespace: getUserNamespace,
  initialNamespace: makeGetInitialNamespace(),
  parameterType: makeGetContactsManagerParameterType(),
}));

const mapDispatchToProps = makeMapDispatchToProps<ImportContactsCsvDialogContainerProps>()(({panelId}) => ({
  onToggleInfoBanner: () => toggleExpandItemAction(infoBoxExpandedKey, isInfoBoxExpandedDefault),
  uploadContactsCsvAsync: (file: File, namespace: string, dryRun?: boolean) =>
    contactsManagerUploadContactsCsvAsyncAction(panelId, namespace, file, dryRun),
  clearFile: contactsManagerClearFileAction,
  deleteJob: jobDeletedAction,
}));

export const ImportContactsCSVDialogContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps) => ({
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    clearJobs: () => {
      batch(() => {
        stateProps.importContactsCSVJobs.forEach((importJob) => dispatchProps.deleteJob(importJob.id));
      });
    },
  }),
)(ImportContactsCSVDialog);
